/**
 * Shared formatting utilities for consistent display across the app.
 * Use these instead of defining local formatCurrency/formatDate in each component.
 */

export let globalCompanyTimezone: string | undefined = undefined;

export function setCompanyTimezone(tz: string) {
  globalCompanyTimezone = tz;
}

export function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(num);
}

/** Like formatCurrency but always shows cents (e.g. $1,234.00). Use for financial/accounting displays. */
/** Abbreviated currency for dashboards: $1.2M / $45.3K / $842.50. Sub-$1K shows 2 decimals. */
export function formatCurrencyAbbrev(val: number | null | undefined): string {
  if (val == null || isNaN(val)) return '$0.00';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(1)}K`;
  return `$${val.toFixed(2)}`;
}

/** Format a percentage to 2 decimal places: "12.50%". */
export function formatPercent(val: number | null | undefined): string {
  if (val == null || isNaN(val)) return '0.00%';
  return `${val.toFixed(2)}%`;
}

/** Format hours to 2 decimal places: "42.00h". */
export function formatHours(val: number | null | undefined): string {
  if (val == null || isNaN(val)) return '0.00h';
  return `${val.toFixed(2)}h`;
}

export function formatCurrencyPrecise(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

export function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Use for calendar dates (like Date of Loss) that correspond to events happening
 * in the real world physical timezone of the company itself.
 */
export function formatCompanyDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return "-";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "Invalid Date";
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: globalCompanyTimezone || "UTC",
    }).format(d);
  } catch (e) {
    return "Invalid Date";
  }
}

export function formatShortDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Short version of company calendar date */
export function formatShortCompanyDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return "-";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "Invalid Date";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: globalCompanyTimezone || "UTC",
    }).format(d);
  } catch (e) {
    return "Invalid Date";
  }
}

/**
 * Format a date-only string (YYYY-MM-DD) from a PostgreSQL date column.
 * Parses by splitting to avoid timezone shifts. Do NOT use for timestamp columns.
 */
export function formatDateOnly(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  const [year, month, day] = dateString.split("-").map(Number);
  if (!year || !month || !day) return "Invalid Date";
  const d = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(d);
}

export function formatShortDateOnly(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  const [year, month, day] = dateString.split("-").map(Number);
  if (!year || !month || !day) return "Invalid Date";
  const d = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

/** For <input type="date"> value from a date-only DB column (already YYYY-MM-DD string). */
export function formatDateOnlyForInput(dateString: string | null | undefined): string {
  if (!dateString) return "";
  return dateString.split("T")[0];
}

export function formatDateTime(dateString: string | Date | null | undefined): string {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
